import { useState, useMemo, useCallback, memo } from "react";
import { X, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import CorrectionItem from "./CorrectionItem";
import RichTextBlockEleve from '../../components/VueQuestion/RichTextBlockEleve';
import { compareAnswers, optionsDeFiche } from '../../utils/questionUtils';

// ─── Constantes hoistées hors du render ───

// Mapping type de réponse EvalNat → sous-type association (EvalNat n'a pas contenu.subType)
const SUB_TYPE_MAP = { paire: 'relier', ordre: 'ordonner', trou: 'texte_trou', trou_meta: 'texte_trou', categorie: 'classer' };

const CATEGORY_CONFIGS = {
    'Automatisme': { bgColor: 'from-emerald-500 to-green-600', icon: '⚙️' },
    'Résolution de problèmes': { bgColor: 'from-red-500 to-pink-600', icon: '🧩' },
    'Lexique': { bgColor: 'from-purple-500 to-indigo-600', icon: '📚' },
    'Compétences langagières et linguistiques': { bgColor: 'from-blue-500 to-cyan-600', icon: '✍️' },
    "Compréhension de l'écrit Arachné": { bgColor: 'from-indigo-500 to-purple-600', icon: '📖' },
    "Compréhension de l'écrit Document Sommeil": { bgColor: 'from-indigo-500 to-purple-600', icon: '📖' },
    "Compréhension de l'écrit Gaspillage": { bgColor: 'from-indigo-500 to-purple-600', icon: '📖' },
    "Compréhension de l'écrit Petit Chose": { bgColor: 'from-teal-500 to-cyan-600', icon: '📖' },
    "Compréhension de l'écrit Volcans sous la glace": { bgColor: 'from-indigo-500 to-purple-600', icon: '📖' },
};

const DEFAULT_CATEGORY = { bgColor: 'from-gray-500 to-gray-600', icon: '📝' };

const getScoreStyle = (pct) => {
    if (pct >= 90) return { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/40', emoji: '🌟', msg: 'Excellent !', sub: 'Tu maîtrises très bien' };
    if (pct >= 75) return { color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/40', emoji: '🔥', msg: 'Très bien !', sub: 'Encore un petit effort' };
    if (pct >= 60) return { color: 'text-lime-400', bg: 'bg-lime-500/10 border-lime-500/40', emoji: '💪', msg: 'Bon travail !', sub: "Continue à t'entraîner" };
    if (pct >= 40) return { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/40', emoji: '🧠', msg: 'Continue !', sub: "Un peu d'entraînement" };
    return { color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/40', emoji: '🌱', msg: 'On y arrivera !', sub: "On s'entraîne ensemble" };
};

// ─── Sous-composant : Grille de chaleur ───

const HeatGrid = memo(function HeatGrid({ items, selectedItem, onSelect }) {
    return (
        <div className="grid grid-cols-5 sm:grid-cols-7 lg:grid-cols-10 gap-2">
            {items.map(({ key, num, isCorrect }, i) => {
                const isSelected = selectedItem === key;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onSelect(key)}
                        className={`
                            relative flex flex-col items-center justify-center p-2 sm:p-3 rounded-xl font-bold text-sm
                            transition-all duration-300 cursor-pointer
                            ${isCorrect
                                ? isSelected
                                    ? 'bg-emerald-500 text-white ring-2 ring-white shadow-lg shadow-emerald-500/40 scale-110'
                                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 hover:scale-105'
                                : isSelected
                                    ? 'bg-red-500 text-white ring-2 ring-white shadow-lg shadow-red-500/40 scale-110'
                                    : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 hover:scale-105'
                            }
                        `}
                        style={{ animation: 'fadeInUp 0.4s ease-out both', animationDelay: `${i * 50}ms` }}
                    >
                        <span className="text-xs opacity-60">Q{num}</span>
                        <span className="text-lg">{isCorrect ? '✓' : '✗'}</span>
                    </button>
                );
            })}
        </div>
    );
});

// ─── Sous-composant : Mode Flashcard ───

const FlashcardMode = memo(function FlashcardMode({ wrongQuestions, ItemBilanDetail, onClose }) {
    const [index, setIndex] = useState(0);

    const current = wrongQuestions[index];
    if (!current) return null;

    const question = current.question;
    const catConfig = CATEGORY_CONFIGS[current.cat] || DEFAULT_CATEGORY;

    const handleNav = (newIndex) => {
        setIndex(newIndex);
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/98 backdrop-blur-xl flex flex-col">
            {/* Header flashcard */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">🔄</span>
                    <div>
                        <p className="text-white font-bold text-sm sm:text-base">
                            Erreur {index + 1}/{wrongQuestions.length}
                        </p>
                        <p className="text-slate-500 text-xs">{current.cat}</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Dots de progression */}
            <div className="flex justify-center gap-1.5 py-3">
                {wrongQuestions.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => handleNav(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                            i === index ? 'bg-white scale-125' : 'bg-slate-700 hover:bg-slate-600'
                        }`}
                    />
                ))}
            </div>

            {/* Card de correction */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                <div
                    key={index}
                    className="max-w-2xl mx-auto space-y-4"
                    style={{ animation: 'slideIn 0.3s ease-out' }}
                >
                    {/* Énoncé */}
                    <div className="bg-slate-800/80 border border-slate-700 rounded-2xl overflow-hidden">
                        <div className={`p-3 bg-gradient-to-r ${catConfig.bgColor} flex items-center gap-2`}>
                            <span>{catConfig.icon}</span>
                            <h3 className="font-bold text-white text-sm">
                                {question?.question_num || `Q${index + 1}`} — {question?.label || question?.descripteur_titre || 'Question'}
                            </h3>
                        </div>
                        <div className="p-4 text-slate-200">
                            {question?.image ? (
                                <img src={question.image} alt="Question" className="max-w-full rounded-lg" />
                            ) : (
                                <RichTextBlockEleve
                                    texte={question?.texte || question?.question}
                                    elements={question?.elements_additionnels}
                                />
                            )}
                        </div>
                    </div>

                    {/* Correction — flip géré en interne par CorrectionItem */}
                    <div className="bg-slate-800/80 border border-slate-700 rounded-2xl overflow-hidden">
                        <div className="p-4">
                            <CorrectionItem
                                itemId={current.key}
                                ItemBilanDetail={ItemBilanDetail}
                                testSpecifique={current.cat}
                                typeQuestion={current.question?.type_question || current.question?.type}
                                subType={current.question?.contenu?.subType || current.question?._subType}
                                suffixe={current.question?.contenu?.suffixe || current.question?.reponses?.[0]?.suffixe}
                                contenu={current.question?.contenu}
                                allowFlip={!current.isCorrect}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation bas */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-slate-800">
                <button
                    onClick={() => handleNav(Math.max(0, index - 1))}
                    disabled={index === 0}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                        bg-slate-800 text-slate-300 hover:bg-slate-700 transition
                        disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ChevronLeft size={16} /> Précédent
                </button>

                {index === wrongQuestions.length - 1 ? (
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm
                            bg-gradient-to-r from-emerald-500 to-teal-500 text-white
                            hover:from-emerald-600 hover:to-teal-600 transition shadow-lg shadow-emerald-500/30"
                    >
                        Terminé
                    </button>
                ) : (
                    <button
                        onClick={() => handleNav(Math.min(wrongQuestions.length - 1, index + 1))}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                            bg-gradient-to-r from-blue-500 to-cyan-500 text-white
                            hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                    >
                        Suivant <ChevronRight size={16} />
                    </button>
                )}
            </div>
        </div>
    );
});


// ─── Composant principal ───

function CorrectionEval({ evalBilan, eleveId }) {

    // ─── Données élève ───
    const ItemBilanDetail = useMemo(() => {
        return evalBilan.eleves_reponses?.find(eleve => String(eleve.id_eleve) === String(eleveId));
    }, [evalBilan.eleves_reponses, eleveId]);

    // ─── Index des questions par ID (lookup rapide) ───
    const questionsById = useMemo(() => {
        const qs = evalBilan.questions || [];
        const isSession = evalBilan.source === 'session';
        const map = {};
        qs.forEach(q => {
            const raw = isSession ? (q.banque_question || q) : q;
            let enrichments = {};

            // BanqueQuestion multi → type_question enrichi
            if (isSession && raw.contenu?.multi && !raw.type_question) {
                enrichments.type_question = 'qcm_multiple';
            }

            // EvalNat association → subType dérivé depuis reponses[0].type
            if (!isSession && raw.type_question === 'association' && raw.reponses?.length) {
                enrichments._subType = SUB_TYPE_MAP[raw.reponses[0]?.type] || 'relier';
            }

            const question = Object.keys(enrichments).length > 0 ? { ...raw, ...enrichments } : raw;
            map[String(question.id)] = question;
        });
        return map;
    }, [evalBilan]);

    // ─── Catégories disponibles ───
    const availableCategories = useMemo(() => {
        if (!ItemBilanDetail?.reponses) return [];
        return Object.keys(ItemBilanDetail.reponses).filter(
            cat => Object.keys(ItemBilanDetail.reponses[cat]).length > 0
        );
    }, [ItemBilanDetail]);

    // ─── State ───
    const [testSpecifique, setTestSpecifique] = useState(
        availableCategories.length > 0 ? availableCategories[0] : "Automatisme"
    );
    const [selectedItem, setSelectedItem] = useState(null);
    const [flashcardMode, setFlashcardMode] = useState(false);

    // ─── Données dérivées ───
    const currentQuestions = questionsById; // Lookup direct par ID, plus besoin de grouper par catégorie
    const categoryConfig = CATEGORY_CONFIGS[testSpecifique] || DEFAULT_CATEGORY;

    // Score global
    const scoreStats = useMemo(() => {
        if (!ItemBilanDetail?.reponses) return { correct: 0, total: 0, percentage: 0 };
        let correct = 0, total = 0;
        Object.values(ItemBilanDetail.reponses).forEach(items => {
            Object.values(items).forEach(value => {
                total++;
                if (compareAnswers(value.réponse, value.bonne_reponse, value.bonne_reponse_image, optionsDeFiche(value))) correct++;
            });
        });
        return { correct, total, percentage: total > 0 ? Math.round((correct / total) * 100) : 0 };
    }, [ItemBilanDetail]);

    // Items de la grille pour la catégorie active
    const gridItems = useMemo(() => {
        if (!ItemBilanDetail?.reponses[testSpecifique]) return [];
        return Object.entries(ItemBilanDetail.reponses[testSpecifique]).map(([key, value], index) => {
            return {
                key,
                num: index + 1,
                isCorrect: compareAnswers(value.réponse, value.bonne_reponse, value.bonne_reponse_image, optionsDeFiche(value)),
            };
        });
    }, [ItemBilanDetail, testSpecifique]);

    // Questions fausses pour le mode flashcard (toutes catégories)
    const wrongQuestions = useMemo(() => {
        if (!ItemBilanDetail?.reponses) return [];
        const wrong = [];
        Object.entries(ItemBilanDetail.reponses).forEach(([cat, items]) => {
            Object.entries(items).forEach(([key, value]) => {
                if (!compareAnswers(value.réponse, value.bonne_reponse, value.bonne_reponse_image, optionsDeFiche(value))) {
                    wrong.push({ cat, key, ...value, question: questionsById[key] });
                }
            });
        });
        return wrong;
    }, [ItemBilanDetail, questionsById]);

    // Nombre d'erreurs dans la catégorie active
    const wrongCountInCategory = useMemo(() => gridItems.filter(i => !i.isCorrect).length, [gridItems]);

    // ─── Handlers ───
    const handleCategoryClick = useCallback((cat) => {
        setTestSpecifique(cat);
        setSelectedItem(null);
    }, []);

    const [isTransitioning, setIsTransitioning] = useState(false);
    const handleCellClick = useCallback((key) => {
        setIsTransitioning(true);
        // Petit délai pour laisser le fade out se faire avant de changer le contenu
        setTimeout(() => {
            setSelectedItem(prev => prev === key ? null : key);
            setIsTransitioning(false);
        }, 150);
    }, []);

    const scoreStyle = getScoreStyle(scoreStats.percentage);

    // Question sélectionnée pour le drawer (selectedItem = questionId string)
    const selectedQuestion = selectedItem ? currentQuestions[selectedItem] : null;
    const selectedReponseData = ItemBilanDetail?.reponses?.[testSpecifique]?.[selectedItem];
    const selectedIsCorrect = selectedReponseData
        ? compareAnswers(selectedReponseData.réponse, selectedReponseData.bonne_reponse, selectedReponseData.bonne_reponse_image, optionsDeFiche(selectedReponseData))
        : false;

    return (
        <div className="min-h-screen bg-slate-950 p-3 sm:p-6 relative overflow-hidden">
            {/* Keyframes pour animations */}
            <style>{`
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes progressGrow { from { width: 0; } }
                @keyframes pulseGlow { 0%, 100% { box-shadow: 0 0 20px rgba(239,68,68,0.3); } 50% { box-shadow: 0 0 30px rgba(239,68,68,0.5); } }
                @keyframes flipIn { from { opacity: 0; transform: rotateY(90deg) scale(0.95); } to { opacity: 1; transform: rotateY(0) scale(1); } }
            `}</style>

            {/* Fond animé */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            <div className="relative z-10 max-w-4xl mx-auto space-y-4 sm:space-y-6">

                {/* ═══ HEADER — Score + Progress bar ═══ */}
                <div className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-4 sm:p-6"
                    style={{ animation: 'fadeInUp 0.5s ease-out' }}>
                    <div className="flex items-center gap-4 sm:gap-6">
                        {/* Emoji + Score */}
                        <div className="flex items-center gap-3">
                            <span className="text-4xl sm:text-5xl">{scoreStyle.emoji}</span>
                            <div>
                                <p className={`text-3xl sm:text-4xl font-extrabold ${scoreStyle.color}`}>
                                    {scoreStats.correct}/{scoreStats.total}
                                </p>
                                <p className={`font-bold text-sm ${scoreStyle.color}`}>{scoreStyle.msg}</p>
                            </div>
                        </div>

                        {/* Progress bar + Message */}
                        <div className="flex-1 hidden sm:block">
                            <div className="flex items-center justify-between mb-1.5">
                                <p className="text-slate-400 text-xs">{scoreStyle.sub}</p>
                                <p className={`font-bold text-sm ${scoreStyle.color}`}>{scoreStats.percentage}%</p>
                            </div>
                            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full`}
                                    style={{
                                        width: `${scoreStats.percentage}%`,
                                        animation: 'progressGrow 1.2s ease-out',
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Progress bar mobile */}
                    <div className="mt-3 sm:hidden">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-slate-400 text-xs">{scoreStyle.sub}</p>
                            <p className={`font-bold text-xs ${scoreStyle.color}`}>{scoreStats.percentage}%</p>
                        </div>
                        <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full"
                                style={{ width: `${scoreStats.percentage}%`, animation: 'progressGrow 1.2s ease-out' }}
                            />
                        </div>
                    </div>
                </div>

                {/* ═══ TABS CATÉGORIES ═══ */}
                <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur-lg py-2 -mx-3 px-3 sm:-mx-6 sm:px-6 rounded-xl">
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                        {availableCategories.map(category => {
                            const config = CATEGORY_CONFIGS[category] || DEFAULT_CATEGORY;
                            const isActive = testSpecifique === category;
                            const catResponses = ItemBilanDetail?.reponses[category] || {};
                            const catTotal = Object.keys(catResponses).length;
                            const catCorrect = Object.values(catResponses).filter(v =>
                                compareAnswers(v.réponse, v.bonne_reponse, v.bonne_reponse_image, optionsDeFiche(v))
                            ).length;

                            return (
                                <button
                                    key={category}
                                    onClick={() => handleCategoryClick(category)}
                                    className={`
                                        flex-shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium
                                        transition-all duration-300
                                        ${isActive
                                            ? `bg-gradient-to-r ${config.bgColor} text-white shadow-lg`
                                            : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700 border border-slate-700/50'
                                        }
                                    `}
                                >
                                    <span>{config.icon}</span>
                                    <span className="hidden sm:inline">{category}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                        isActive ? 'bg-white/20' : 'bg-slate-700'
                                    }`}>
                                        {catCorrect}/{catTotal}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ═══ GRILLE DE CHALEUR ═══ */}
                <div className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-4 sm:p-6"
                    style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-white font-bold flex items-center gap-2">
                            <span className="text-lg">{categoryConfig.icon}</span>
                            <span className="text-sm sm:text-base">{testSpecifique}</span>
                        </h2>
                        <p className="text-slate-500 text-xs">Clique sur une case</p>
                    </div>

                    <HeatGrid
                        items={gridItems}
                        selectedItem={selectedItem}
                        onSelect={handleCellClick}
                    />

                    {/* Bouton "Revoir mes erreurs" */}
                    {wrongCountInCategory > 0 && (
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={() => setFlashcardMode(true)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm
                                    bg-gradient-to-r from-red-500 to-orange-500 text-white
                                    hover:from-red-600 hover:to-orange-600 transition-all
                                    shadow-lg hover:shadow-xl hover:scale-105"
                                style={{ animation: 'pulseGlow 2s ease-in-out infinite' }}
                            >
                                <RotateCcw size={16} />
                                Revoir mes {wrongQuestions.length} erreur{wrongQuestions.length > 1 ? 's' : ''}
                            </button>
                        </div>
                    )}
                </div>

                {/* ═══ DRAWER DE DÉTAIL — Face A (ma réponse) ↔ Face B (le corrigé) ═══ */}
                {isTransitioning && (
                    <div className="flex items-center justify-center py-8 animate-pulse">
                        <div className="w-8 h-8 rounded-full border-2 border-slate-600 border-t-blue-400 animate-spin" />
                    </div>
                )}
                {!isTransitioning && selectedItem && !selectedQuestion && (
                    <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 text-center">
                        <p className="text-slate-400 text-sm">Cette question est introuvable ou n'existe plus.</p>
                        <button onClick={() => setSelectedItem(null)} className="mt-3 text-xs text-slate-500 hover:text-white transition">Fermer</button>
                    </div>
                )}

                {!isTransitioning && selectedItem && selectedQuestion && (
                    <div
                        className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl overflow-hidden"
                        style={{ animation: 'fadeInUp 0.3s ease-out', perspective: '1000px' }}
                    >
                        {/* Header — toujours visible */}
                        <div className={`p-3 bg-gradient-to-r ${categoryConfig.bgColor} flex items-center justify-between`}>
                            <div className="flex items-center gap-2">
                                <span>{categoryConfig.icon}</span>
                                <h3 className="font-bold text-white text-sm">
                                    {selectedQuestion.question_num || `Q${(gridItems.findIndex(g => g.key === selectedItem) + 1) || '?'}`} — {selectedQuestion.label || selectedQuestion.descripteur_titre || 'Question'}
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelectedItem(null)}
                                className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Énoncé compact — toujours visible */}
                        <div className="px-4 py-3 bg-slate-900/50 border-b border-slate-700/50 text-slate-300 text-sm">
                            {selectedQuestion.image ? (
                                <img src={selectedQuestion.image} alt="Question" className="max-w-full max-h-40 rounded-lg object-contain" />
                            ) : (
                                <RichTextBlockEleve
                                    texte={selectedQuestion.texte || selectedQuestion.question}
                                    elements={selectedQuestion.elements_additionnels}
                                />
                            )}
                        </div>

                        {/* Zone flip — Face A (réponse) ↔ Face B (corrigé) */}
                        <div className="p-4">
                            <CorrectionItem
                                itemId={selectedItem}
                                ItemBilanDetail={ItemBilanDetail}
                                testSpecifique={testSpecifique}
                                typeQuestion={selectedQuestion?.type_question || selectedQuestion?.type}
                                subType={selectedQuestion?.contenu?.subType || selectedQuestion?._subType}
                                suffixe={selectedQuestion?.contenu?.suffixe || selectedQuestion?.reponses?.[0]?.suffixe}
                                contenu={selectedQuestion?.contenu}
                                allowFlip={!selectedIsCorrect}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ═══ MODE FLASHCARD (overlay) ═══ */}
            {flashcardMode && wrongQuestions.length > 0 && (
                <FlashcardMode
                    wrongQuestions={wrongQuestions}
                    ItemBilanDetail={ItemBilanDetail}
                    onClose={() => setFlashcardMode(false)}
                />
            )}
        </div>
    );
}

export default CorrectionEval;
